
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "./use-toast";

export interface ChatHistoryItem {
  id: string;
  title: string;
  last_message?: string;
  created_at: Date;
  updated_at: Date;
  context_type: string;
  context_key?: string;
  messages: Array<{ role: 'user' | 'assistant'; content: string; timestamp: Date }>;
}

export const useChatHistory = () => {
  const [conversations, setConversations] = useState<ChatHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchChatHistory = async () => {
    try {
      const { data: conversationsData, error } = await supabase
        .from('conversations')
        .select(`
          id,
          title,
          created_at,
          updated_at,
          context_type,
          context_key,
          chat_messages(content, sender, timestamp)
        `)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      const formattedConversations = conversationsData.map(conv => ({
        id: conv.id,
        title: conv.title,
        last_message: conv.chat_messages?.[0]?.content,
        created_at: new Date(conv.created_at),
        updated_at: new Date(conv.updated_at),
        context_type: conv.context_type,
        context_key: conv.context_key,
        messages: (conv.chat_messages || []).map((msg: any) => ({
          role: msg.sender || 'assistant', // Map sender from DB to role in the app
          content: msg.content || '',
          timestamp: new Date(msg.timestamp)
        }))
      }));

      setConversations(formattedConversations);
    } catch (error: any) {
      toast({
        title: "Error fetching chat history",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchChatHistory();
  }, []);

  return { conversations, isLoading, refreshHistory: fetchChatHistory };
};
